import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import FeedForm from './components/FeedForm';
import { formFeedsApi } from '../../services/formFeedsApi';
import toast from 'react-hot-toast';
import Skeleton from '../../components/Skeleton';

const EditFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedData, setFeedData] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchFeed();
    }
  }, [id]);

  const fetchFeed = async () => {
    try {
      setLoading(true);
      const data = await formFeedsApi.getById(Number(id));
      setFeedData(data);
    } catch (error: any) {
      toast.error('Failed to load feed');
      navigate('/form-feeds');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setIsLoading(true);
      await formFeedsApi.update(Number(id), {
        name: formData.name,
        is_active: formData.is_active,
        on_submit: formData.on_submit,
        create_or_update: formData.create_or_update,
        do_not_update_if_exists: formData.do_not_update_if_exists,
      });
      toast.success('Feed updated successfully');
      navigate('/form-feeds');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update feed');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="mb-6 space-y-3">
          <Skeleton width={120} height={16} variant="rounded" />
          <Skeleton width={200} height={28} variant="rounded" />
          <Skeleton width={280} height={14} variant="rounded" />
        </div>
        <div className="theme-bg-primary rounded-lg shadow-sm border theme-border p-6 max-w-2xl space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton width={100} height={14} variant="rounded" />
              <Skeleton height={38} variant="rounded" />
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <Skeleton height={38} variant="rounded" className="flex-1" />
            <Skeleton width={80} height={38} variant="rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <button
          onClick={() => navigate('/form-feeds')}
          className="flex items-center gap-2 theme-text-secondary hover:theme-text-primary transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Feeds
        </button>
        <h1 className="text-2xl font-bold theme-text-primary">Edit Feed</h1>
        <p className="text-sm theme-text-tertiary mt-1">
          Update feed configuration and settings
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="theme-bg-primary rounded-lg shadow-sm border theme-border p-6 max-w-2xl"
      >
        <FeedForm
          initialData={feedData}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/form-feeds')}
          isLoading={isLoading}
        />
      </motion.div>
    </div>
  );
};

export default EditFeedPage;
